import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { UI_CONSTANTS } from "../../utils/constants";
import { useLanguage } from "../../context/LanguageContext";
import { getLocalizedFieldValue } from "../../utils/language";
import he from "he";
import { Link, useNavigate } from "react-router-dom";


const slugify = (text = "") =>
  text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/<\/?[^>]+(>|$)/g, "") // remove html
    .replace(/\s+/g, "-")           // spaces to -
    .replace(/[^\w\-]+/g, "")       // remove symbols
    .replace(/\-\-+/g, "-");        // remove --


const buildMediaUrl = (path = "") => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = (process.env.REACT_APP_API || "").replace(/\/+$/, "");
  if (!base) return path;
  const normalizedBase = base.replace(/\/v1$/i, "");
  return `${normalizedBase}/${path.replace(/^\/+/, "")}`;
};

// Function to strip HTML tags and decode entities, then truncate
const truncateText = (text = "", maxLength = 120) => {
  if (!text) return "";
  
  // Decode HTML entities first
  const decoded = he.decode(text);
  
  // Strip HTML tags by creating a temporary element
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = decoded;
  const plainText = tempDiv.textContent || tempDiv.innerText || "";
  
  // Truncate if needed
  if (plainText.length <= maxLength) return plainText;
  return plainText.substring(0, maxLength).trim() + "...";
};

function LatestNews() {
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Popup States
  const [showVideoPopup, setShowVideoPopup] = useState(false);
  const [popupVideoUrl, setPopupVideoUrl] = useState("");
  const [copiedVideoUrl, setCopiedVideoUrl] = useState("");

  const { language } = useLanguage();
  const navigate = useNavigate();

  const normalizeNewsData = (data) => {
    if (!data) return [];
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.news)) return data.news;
    if (Array.isArray(data)) return data;
    return [];
  };

  const fetchNews = useCallback(async () => {
    setLoading(true);
    try {
      const response = await handleApiCall("get", endpoints.newsList);
      if (
        response?.data?.code === 200 ||
        response?.data?.code === "200" ||
        response?.data?.success === true ||
        response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS
      ) {
        const responseData = response?.data?.data;
        setNewsItems(normalizeNewsData(responseData));
      } else if (response?.error) {
        console.error("Error fetching news list:", response?.message);
        setNewsItems([]);
      } else {
        setNewsItems([]);
      }
    } catch (error) {
      console.error("Error fetching news list:", error);
      setNewsItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  // useEffect(() => {
  //   if (copiedVideoUrl) {
  //     console.log("Copied URL changed:", copiedVideoUrl);
  //   }
  // }, [copiedVideoUrl]);

  const slides = useMemo(() => {
    if (loading) {
      return [
        <SplideSlide key="loading" className="w-[320px] md:w-[450px] lg:w-[600px]">
          <div className="flex h-[270px] w-full items-center justify-center text-white text-lg bg-neutral-800 rounded-xl">
            {UI_CONSTANTS.BUTTON_LABELS.LOADING || "Loading..."}
          </div>
        </SplideSlide>,
      ];
    }

    if (!newsItems.length) {
      return [
        <SplideSlide key="empty" className="w-[320px] md:w-[450px] lg:w-[600px]">
          <div className="flex h-[270px] w-full items-center justify-center text-white text-lg bg-neutral-800 rounded-xl">
            {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
          </div>
        </SplideSlide>,
      ];
    }

  const actionBtnClass =
    "mt-2 md:mt-4 md:px-5 md:py-2 inline-flex items-center justify-center " +
    "bg-saudiaGreen text-white rounded-sm md:rounded-md " +
    "w-[76px] md:w-[8.875rem] h-6 md:h-11 " +
    "text-[10px] md:text-lg font-SaudiaLight";

  const handleCopyVideoUrl = async (url) => {
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
    console.log("urlurlurlurl", url);
    

    setCopiedVideoUrl(url);

    setTimeout(() => {
      setCopiedVideoUrl("");
    }, 2000);
  };

    return newsItems.map((item, index) => {
      const title =
        getLocalizedFieldValue(item, language, "title") ||
        UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND;

      const fullDescription =
        getLocalizedFieldValue(item, language, "discription") ||
        UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND;
      const description = truncateText(fullDescription, 120);

      const isVideo = (item?.type || "").toLowerCase() === "video";
      // const mediaUrl = buildMediaUrl(item?.news);
      const mediaUrl = buildMediaUrl(
        item?.type?.toLowerCase() === "video" ? item?.desktop_news : item?.desktop_news
      );
      // console.log("item saurabh", item);

      return (
        <SplideSlide key={item?._id || item?.id || index} className="w-[320px] md:w-[450px] lg:w-[600px]">
          <div className="relative h-[180px] md:h-[270px] lg:h-[334px] rounded-xl overflow-hidden bg-neutral-300 shadow-xl item">
            {mediaUrl ? (
              isVideo ? (
                <video
                  className="absolute inset-0 w-full h-full object-cover"
                  src={mediaUrl}
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls={false}
                  preload="metadata"
                />
              ) : (
                <>
                  <picture className="absolute inset-0 w-full h-full">
                    <source media="(min-width: 768px)" srcSet={item.desktop_news} />
                    <img
                      src={item.mobile_news}
                      alt={title}
                      className="w-full h-full object-cover"
                    />
                  </picture>
                </>
              )
            ) : null}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/80" />
          </div>

          {/* TEXT & BUTTON */}
          <div className={`absolute caption px-3 ${language === "AR" ? "right-3 md:right-5" : "left-3 md:left-5"}`}>
            <h3 className="text-white text-lg md:text-[1.75rem] font-SaudiaBold">{title}</h3>
            <p className={`text-white ${language === "AR" ? "pl-4 md:pl-20 md:mt-3 font-SaudiaLight" : "pr-4 md:pr-20 md:mt-1 font-SaudiaLight"} md:mt-1 text-xs md:text-base lg:text-lg leading-snug`}>
              {description}
            </p>

            {isVideo ? (
              // <button
              //   onClick={() => {
              //     setPopupVideoUrl(mediaUrl);
              //     setShowVideoPopup(true);
              //   }}
              //   className="mt-2 md:mt-4 md:px-5 md:py-2 inline-flex items-center justify-center bg-saudiaGreen text-white rounded-sm md:rounded-md w-[76px] md:w-[8.875rem] h-5 md:h-11 text-[10px] md:text-lg font-SaudiaLight md:font-SaudiaRegular"
              // >
              //   {language === "AR" ? "شاهد الآن" : (UI_CONSTANTS.BUTTON_LABELS.PLAY_NOW || "Play now")}
              // </button>
               <div className="flex items-center gap-2 mt-2 md:mt-4">
                  {/* PLAY NOW */}
                  {/* <button
                    onClick={() => {
                      setPopupVideoUrl(mediaUrl);
                      setShowVideoPopup(true);
                    }}
                    className={actionBtnClass}
                  >
                    {language === "AR" ? "شاهد الآن" : "Play now"}
                  </button> */}

                   {/* <button
                    onClick={() => {
                      const videoUrl = encodeURIComponent(item.desktop_news);
                      const videoId = item._id;
                      const fullUrl = `${window.location.origin}/video/${videoId}?src=${videoUrl}`;
                      window.open(fullUrl, "_blank", "noopener,noreferrer");
                    }}
                    className={actionBtnClass}
                  >
                    {language === "AR" ? "شاهد الآن" : "Play now"}
                  </button> */}

                  <button
  onClick={() => {
    const title =
      getLocalizedFieldValue(item, language, "title") || "";
    const slug = slugify(title);
    window.open(
      `/video/${item._id}/${slug}`,
      "_blank",
      "noopener,noreferrer"
    );
    // window.open(`/video/${item._id}`, "_blank", "noopener,noreferrer");
  }}
  className={actionBtnClass}
>
  {language === "AR" ? "شاهد الآن" : "Play now"}
</button>


                  {/* COPY URL */}
                  {/* <button
                    onClick={() => handleCopyVideoUrl(mediaUrl)}
                    className={actionBtnClass}
                  >
                    {language === "AR" ? "نسخ الرابط" : "Copy URL"}
                  </button> */}

                  {/* COPIED TEXT */}
                  {/* {copiedVideoUrl?.trim() === mediaUrl && (
                    <span className="text-green-400 text-[10px] md:text-sm">
                      {language === "AR" ? "تم النسخ" : "Copied"}
                    </span>
                  )} */}
                </div>
            ) : (
              <button 
                onClick={() => {
                  const newsId = item?._id || item?.id;
                  if (newsId) {
                    const fullUrl = `${window.location.origin}/news/${newsId}`;
                    window.open(fullUrl, "_blank", "noopener,noreferrer");
                  }
                }}
                className="mt-2 md:mt-4 md:px-5 md:py-2 bg-saudiaGreen text-white rounded-sm md:rounded-md w-[76px] md:w-[8.875rem] h-6 md:h-11 text-[10px] md:text-lg font-SaudiaLight cursor-pointer"
              >
                {language === "AR" ? "اقرأ المزيد" : (UI_CONSTANTS.BUTTON_LABELS.READ_MORE || "Read more")}
              </button>
            )}
          </div>
        </SplideSlide>
      );
    });
  }, [newsItems, loading, language]);
  const splideRef = useRef(null);

  return (
    <section className="overflow-hidden [&_.splide\_\_track]:!overflow-visible" id="news">
      <div className="md:pt-4 pb-6 px-6 md:px-8 lg:px-8 2xl:px-0 max-w-8xl mx-auto">
        <div className="md:ml-12 lg:ml-28">
          <h2 className={`text-white md:text-[2rem] lg:text-[2.625rem] mb-6 md:mb-10 flex items-center ${language === "AR" ? "text-[22px] font-SaudiaBold" : "text-[28px] font-SaudiaBoldItalic"}`}>
            {/* Latest news & content */}
            {/* {language === "AR" ? "آخر الأخبار والمحتوى" : "Latest news & content"} */}
            {language === "AR" ? "أحدث المحتوى" : "Latest content"}
            <Link to="/latest-content" className={`text-white text-xl font-SaudiaRegular md:top-2.5 relative flex items-center max-[767px]:mt-[5px] gap-2 ${language === "AR" ? "mr-auto md:ml-28" : "ml-auto md:mr-28"}`}>{language === "AR" ? "عرض الكل" : "View All"} <svg width="28" height="17" viewBox="0 0 28 17" fill="none" xmlns="http://www.w3.org/2000/svg" className={`max-[767px]:-mt-[5px] ${language === "AR" ? "rotate-180" : ""}`}>
              <path d="M1.079 7.484H24.3746L18.6559 1.83154C18.4527 1.63033 18.3386 1.35744 18.3386 1.07289C18.3386 0.788342 18.4527 0.515448 18.6559 0.314242C18.8591 0.113036 19.1346 2.12005e-09 19.422 0C19.7093 -2.12005e-09 19.9849 0.113036 20.1881 0.314242L27.741 7.79387C27.8281 7.88775 27.8976 7.99631 27.9461 8.11442C27.9521 8.14979 27.9521 8.18591 27.9461 8.22127C27.9755 8.30767 27.9936 8.39744 28 8.4884V8.55251C27.9995 8.66596 27.9775 8.77831 27.9353 8.88375C27.8905 9.00371 27.8206 9.11294 27.7303 9.20431L20.1773 16.6839C20.077 16.7841 19.9576 16.8636 19.8261 16.9178C19.6946 16.9721 19.5536 17 19.4112 17C19.2687 17 19.1277 16.9721 18.9962 16.9178C18.8647 16.8636 18.7454 16.7841 18.6451 16.6839C18.544 16.5846 18.4637 16.4664 18.4089 16.3362C18.3541 16.206 18.3259 16.0663 18.3259 15.9253C18.3259 15.7842 18.3541 15.6446 18.4089 15.5144C18.4637 15.3842 18.544 15.266 18.6451 15.1666L24.3746 9.62103H1.079C0.79283 9.62103 0.518381 9.50846 0.316029 9.30807C0.113678 9.10769 -1.90735e-06 8.8359 -1.90735e-06 8.55251C-1.90735e-06 8.26913 0.113678 7.99734 0.316029 7.79696C0.518381 7.59657 0.79283 7.484 1.079 7.484Z" fill="white"/>
              </svg>
            </Link>
          </h2>
          <div className="skewSlider nagative mb-6 md:mb-12 [&_.splide\_\_arrows]:absolute
              [&_.splide\_\_arrows]:-top-[84px]
              [&_.splide\_\_arrows]:right-0
              [&_.splide\_\_arrows]:hidden
              md:[&_.splide\_\_arrows]:flex
              [&_.splide\_\_arrows]:gap-x-4
              [&_.splide\_\_arrows_button]:transform-none
              [&_.splide\_\_arrows_button]:border
              [&_.splide\_\_arrows_button]:border-solid
              [&_.splide\_\_arrows_button]:border-white/40
              [&_.splide\_\_arrows_button]:bg-transparent
              [&_.splide\_\_arrows_button]:w-10
              [&_.splide\_\_arrows_button]:h-10
              [&_.splide\_\_arrows_button_svg]:fill-white
              [&_.splide\_\_arrows_button]:opacity-100
              [&_.splide\_\_arrows_button]:rounded-sm
              [&_.splide\_\_arrows_button]:relative
              [&_.splide\_\_arrows_button]:left-0
              [&_.splide\_\_arrows_button]:right-0
              [&_.splide\_\_arrows_button:not(:disabled)]:bg-saudiaGreen
              [&_.splide\_\_arrows_button:not(:disabled)]:border-saudiaGreen
              max-[767px]:[&_.splide\_\_pagination]:hidden">
            <Splide
              ref={splideRef}
              key={language}
              options={{
                rewind: false,
                gap: "28px",
                perPage: 2,
                autoWidth: true,
                focus: 0,
                start: 0,
                perMove: 1,
                arrows: true,
                pagination: true,
                direction: language === "AR" ? "rtl" : "ltr",
                breakpoints: {
                  767: {
                    gap: "12px",
                  },
                },
              }}
              aria-label="Latest News"
            >
              {slides}
            </Splide>
          </div>
        </div>
      </div>

      {/* ---------------------- VIDEO POPUP MODAL ---------------------- */}
      {showVideoPopup && popupVideoUrl && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999]"
          onClick={() => setShowVideoPopup(false)}
        >
          <div
            className="relative w-[85%] max-w-4xl bg-black rounded-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              key={popupVideoUrl}
              src={popupVideoUrl}
              controls
              autoPlay
              playsInline
              className="w-full h-auto"
            />

            <button
              onClick={() => {
                setShowVideoPopup(false);
                setPopupVideoUrl("");
              }}
              className="absolute top-2 right-2 text-white bg-red-600 rounded-full px-3 py-1"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export default LatestNews;
